import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { SearchableSelect } from '../ui/SearchableSelect';
import { ModalSuccess } from '../training/ModalSuccess';
import { trainingProducts } from '../../data/trainingProducts';
import { workerTrainingStore } from '../../services/workerTrainingStore';
import { courseExpiryDate, formatItDate, validityLabel } from '../../utils/workers';
export interface AssignableWorker {
  id: number;
  name: string;
  role?: string;
  company?: string;
}
interface AssignTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
  workers: AssignableWorker[];
  // Aggregated view: lets the user narrow the list by company
  showCompanyFilter?: boolean;
}
interface RowState {
  done: boolean;
  date: string;
}

// DIRETTA: a course from the "Formazione" database associated to one or more workers
export function AssignTrainingModal({
  isOpen,
  onClose,
  workers,
  showCompanyFilter = false
}: AssignTrainingModalProps) {
  const [productCode, setProductCode] = useState('');
  const [rows, setRows] = useState<Record<number, RowState>>({});
  const [query, setQuery] = useState('');
  const [company, setCompany] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    setProductCode('');
    setRows({});
    setQuery('');
    setCompany('');
    setErrors({});
    setSaved(0);
  }, [isOpen]);
  const companies = useMemo(() => Array.from(new Set(workers.map(w => w.company).filter(Boolean))) as string[], [workers]);
  const q = query.trim().toLowerCase();
  const visible = workers.filter(w => (!company || w.company === company) && (!q || `${w.name} ${w.role || ''}`.toLowerCase().includes(q)));
  const selectedIds = Object.keys(rows).map(Number);
  const toggle = (id: number) => setRows(prev => {
    const next = {
      ...prev
    };
    if (next[id]) delete next[id];else next[id] = {
      done: true,
      date: ''
    };
    return next;
  });
  const updateRow = (id: number, changes: Partial<RowState>) => {
    setRows(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...changes
      }
    }));
    setErrors(e => ({
      ...e,
      [`date-${id}`]: ''
    }));
  };
  const save = () => {
    const next: Record<string, string> = {};
    if (!productCode) next.product = 'Seleziona il corso';
    if (selectedIds.length === 0) next.workers = 'Seleziona almeno un lavoratore';
    selectedIds.forEach(id => {
      if (rows[id].done && !rows[id].date) next[`date-${id}`] = 'Inserisci la data di erogazione';
    });
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    const product = trainingProducts.find(p => p.code === productCode)!;
    const stamp = Date.now();
    workerTrainingStore.addMany(selectedIds.map(id => {
      const row = rows[id];
      return {
        workerId: id,
        course: {
          id: `dir-${stamp}-${id}`,
          name: product.name,
          productCode,
          firstDate: row.done ? row.date : '',
          updateDate: '',
          expiryDate: row.done ? courseExpiryDate(productCode, row.date) : '',
          certificateName: '',
          expiryAlert: true,
          source: 'diretta',
          done: row.done
        }
      };
    }));
    setSaved(selectedIds.length);
    setTimeout(onClose, 1400);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Associa formazione" size="xl" data-id="element-2086" data-mp="3fsa84sxvwzgr">
      <AnimatePresence mode="wait" data-id="element-2087" data-mp="3kqv6snxhnb33">
        {saved > 0 ? <ModalSuccess title="Formazione associata" subtitle={`Registrata nella scheda di ${saved} ${saved === 1 ? 'lavoratore' : 'lavoratori'}`} data-id="element-2088" data-mp="3knzbz8gxt2iv" /> : <div className="space-y-5" data-id="element-2089" data-mp="1qeexfe9ebgu0">
            <div data-id="element-2090" data-mp="3gswhtvlbzi1e">
              <SearchableSelect label="Corso di formazione" value={productCode} onChange={e => {
            setProductCode(e.target.value);
            setErrors(er => ({
              ...er,
              product: ''
            }));
          }} options={trainingProducts.map(p => ({
            value: p.code,
            label: `${p.type} — ${p.name} · ${p.code}`
          }))} placeholder="Cerca nel database Formazione..." error={errors.product} required data-id="element-2091" data-mp="3gscyqbjy2nls" />
              {productCode && <p className="text-xs text-slate-500 mt-1.5" data-id="element-2092" data-mp="36wjhieva65xo">{validityLabel(productCode)}</p>}
            </div>

            <div data-id="element-2093" data-mp="2eexp0htcfw22">
              <div className="flex flex-col sm:flex-row gap-3 mb-3" data-id="element-2094" data-mp="2eefi8r921zfw">
                <div className="flex-1" data-id="element-2095" data-mp="2eckyhg9isdde">
                  <Input aria-label="Cerca lavoratore" placeholder="Cerca per nome o mansione..." startContent={<Search className="h-4 w-4" data-id="element-2097" data-mp="207klhjbgvsng" />} value={query} onChange={e => setQuery(e.target.value)} data-id="element-2096" data-mp="2ae5dfhx01f2m" />
                </div>
                {showCompanyFilter && <div className="sm:w-64" data-id="element-2098" data-mp="1v9sucrincakb">
                    <Select aria-label="Azienda" value={company} onChange={e => setCompany(e.target.value)} options={[{
                value: '',
                label: 'Tutte le aziende'
              }, ...companies.map(c => ({
                value: c,
                label: c
              }))]} data-id="element-2099" data-mp="1vaua39alxl8d" />
                  </div>}
              </div>
              {errors.workers && <p className="text-sm text-red-600 mb-2" role="alert" data-id="element-2100" data-mp="xl0mqpab5w8m">
                  {errors.workers}
                </p>}
              <ul className="border border-slate-100 rounded-xl divide-y divide-slate-100 max-h-[42vh] overflow-y-auto custom-scrollbar" data-id="element-2101" data-mp="358c3g77vtj41">
                {visible.length === 0 && <li className="p-6 text-center text-sm text-slate-400" data-id="element-2102" data-mp="30bbvtrvdw8at">Nessun lavoratore trovato.</li>}
                {visible.map(w => {
              const row = rows[w.id];
              const expiry = row?.done ? courseExpiryDate(productCode, row.date) : '';
              return <li key={w.id} className={`p-3 flex flex-col lg:flex-row lg:items-center gap-3 ${row ? 'bg-orange-50/40' : ''}`} data-id="element-2103" data-mp="2hizloiyho2q4">
                      <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" data-id="element-2104" data-mp="27ktkk3kib0mb">
                        <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={Boolean(row)} onChange={() => toggle(w.id)} data-id="element-2105" data-mp="as1jdnx01ujg" />
                        <span className="min-w-0" data-id="element-2106" data-mp="ibasdg1lfr7z">
                          <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-2107" data-mp="i9zrpp3g2zl3">{w.name}</span>
                          <span className="block text-xs text-slate-500 truncate" data-id="element-2108" data-mp="n4m1xoxsim3l">
                            {[w.role, showCompanyFilter ? w.company : ''].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </label>
                      {row && <div className="flex flex-wrap items-start gap-3 pl-7 lg:pl-0" data-id="element-2109" data-mp="3vk5pc7bs5aaq">
                          <div role="radiogroup" aria-label={`Corso effettuato da ${w.name}`} className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white" data-id="element-2110" data-mp="4e49zmks7uja">
                            {[{
                      v: true,
                      l: 'Fatto'
                    }, {
                      v: false,
                      l: 'Non fatto'
                    }].map(o => <button key={o.l} type="button" role="radio" aria-checked={row.done === o.v} onClick={() => updateRow(w.id, {
                      done: o.v
                    })} className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${row.done === o.v ? 'bg-seform-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-2111" data-mp="20wtu0wnwbsg1">
                                {o.l}
                              </button>)}
                          </div>
                          {row.done && <>
                              <div className="w-44" data-id="element-2112" data-mp="z6orhfe06vc7">
                                <Input aria-label={`Data di erogazione per ${w.name}`} type="date" value={row.date} onChange={e => updateRow(w.id, {
                        date: e.target.value
                      })} error={errors[`date-${w.id}`]} data-id="element-2113" data-mp="z7zql1wi7ewz" />
                              </div>
                              <div className="text-xs pt-2 whitespace-nowrap" data-id="element-2114" data-mp="2s2hi26p077aj">
                                <span className="text-slate-500" data-id="element-2115" data-mp="2x1kh9aiayjal">Scadenza </span>
                                <span className="font-medium text-slate-800" data-id="element-2116" data-mp="2wzhhvzv9fdt7">
                                  {expiry ? formatItDate(expiry) : productCode && row.date ? 'nessuna' : '—'}
                                </span>
                              </div>
                            </>}
                        </div>}
                    </li>;
            })}
              </ul>
            </div>

            <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100" data-id="element-2117" data-mp="4zb0tc4lb9v1">
              <p className="text-sm text-slate-500" data-id="element-2118" data-mp="9sx104tkhl9y">
                {selectedIds.length} {selectedIds.length === 1 ? 'lavoratore selezionato' : 'lavoratori selezionati'}
              </p>
              <div className="flex gap-3" data-id="element-2119" data-mp="2armscg1qzp">
                <Button variant="flat" onClick={onClose} data-id="element-2120" data-mp="nrdhuol58s5t">
                  Annulla
                </Button>
                <Button onClick={save} data-id="element-2121" data-mp="sq6kflyw6yr7">Associa formazione</Button>
              </div>
            </div>
          </div>}
      </AnimatePresence>
    </Modal>;
}